import { useEffect, useState } from 'react'
import accountingReportService from '@api/v1/accountingReportService'

/**
 * The tenant's GL category list, for the Category variants of P&L / Balance
 * Sheet / Trial Balance (CC-49973).
 *
 * ONE hook for every consumer on purpose. V1's gate is two conditions ANDed
 * together — the `AccountingTrackCategories` setting AND a non-empty category
 * list (AccountingController.cs:2232-2251) — and cb-api resolves both in a
 * single call so two pages can never disagree about whether this tenant offers
 * category reporting. Anything that re-derives the gate locally is a bug.
 *
 * `trackCategories` is false until proven true: cb-api fails closed, and this
 * hook starts false so the option is never offered during the in-flight window.
 * That direction matters — see CC-50039. Offering Category P&L when the list
 * hasn't resolved lets a user run a statement with no category columns at all.
 *
 * The `.catch()` below is defence in depth, not a live bug fix.
 * `accountingReportService.categories.list()` already swallows its own errors
 * and returns `{ trackCategories: false, categories: [] }`, so today this hook
 * cannot see a rejection. Without the catch, though, the whole gate's
 * fail-closed behaviour rests on an invariant in a DIFFERENT file: the day that
 * service stops catching, this hook sticks at `loading: true` forever with an
 * unhandled rejection, and a consumer that reads `loading` rather than
 * `trackCategories` would hang instead of refusing. Belt and braces on a gate
 * that protects a financial statement is worth two lines.
 */
export default function useGlCategories() {
  const [state, setState] = useState({ trackCategories: false, categories: [], loading: true })

  useEffect(() => {
    let cancelled = false
    accountingReportService.categories.list()
      .then((r) => {
        if (!cancelled) setState({ ...r, loading: false })
      })
      .catch(() => {
        // Resolve to the REFUSING state, never to a permanent "loading". An
        // unresolvable category list means we do not know what this tenant
        // tracks, and "we don't know" must read as "don't offer it".
        if (!cancelled) setState({ trackCategories: false, categories: [], loading: false })
      })
    return () => { cancelled = true }
  }, [])

  return state
}

/**
 * V1's synthetic "no category assigned" id, mirrored from cb-api
 * (AccountingController.cs:2239-2244). A real selection, never a filler.
 */
export const UNCATEGORIZED_ID = -99

/** All selectable category ids, Uncategorized included. */
export const allCategoryIds = (categories) => (categories || []).map((c) => c.objectIntId)

/**
 * Human-readable category selection for an export/PDF filter stamp — "All", or
 * the picked names. Shared by P&L and Balance Sheet: a category-scoped statement
 * and a firm-wide one over the same window are DIFFERENT statements, so both
 * pages have to stamp which one the file holds, and they must word it the same.
 */
export const categoryNames = (categories, ids) => {
  const picked = ids || []
  if (categories?.length && picked.length === categories.length) return 'All'
  const byId = new Map((categories || []).map((c) => [c.objectIntId, c.name]))
  return picked.map((id) => byId.get(id) || `#${id}`).join(', ')
}

/**
 * The `filteredCategories` wire value: pipe-delimited category int-ids.
 * CB parses it with `filteredCategories.Split('|').Select(int.Parse)`
 * (ExportHelper.cs:3087), so every element must be a bare integer — no spaces,
 * no names, no trailing pipe. An EMPTY selection returns '' which callers must
 * treat as "don't run a category report" rather than "all categories": CB reads
 * '' as no category filter at all and answers with the ordinary statement.
 */
export const toFilteredCategories = (ids) =>
  (ids || [])
    .map((n) => Number(n))
    // A real category id, or the Uncategorized sentinel. `Number(null)` is a
    // perfectly finite 0, so an integer check alone lets a null through as
    // category `0` — an id CB matches nothing against, i.e. a blank statement.
    // cb-api validates the list the same way, so the two ends agree on what a
    // usable id is.
    .filter((n) => Number.isInteger(n) && (n > 0 || n === UNCATEGORIZED_ID))
    .join('|')
